import { mergeWith, round } from "lodash";
import { getIsConsumerOrPersonalApplication } from "@/constants";
import type { LoanDetails, InArrears, ApplicationType } from "@/store/slices/types/applicationFormSlice";
import type { QuoteCalculatorSlice } from "@/store/slices/types/quoteCalculator";
import type { LenderProduct } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import type { UseLenderCardResult } from "@/components/LenderProductCard/hooks/useLenderCard";
import type { UseLenderCardCustomResult } from "@/components/LenderProductCard/hooks/useLenderCardCustom";
import { formatFeesForSaveQuote, replaceArrays } from "@/utils/lenderUtils";

interface BuildLenderLoanDetailsPayloadParams {
  loanDetails: LoanDetails;
  quoteCalculator: QuoteCalculatorSlice;
  product: LenderProduct | undefined;
  lenderCard: UseLenderCardResult;
  lenderCardCustom: UseLenderCardCustomResult;
  isCustomQuote: boolean | undefined;
  inArrears: InArrears;
  applicationType: ApplicationType;
}

const buildLenderLoanDetailsPayload = ({
  loanDetails,
  quoteCalculator,
  product,
  lenderCard,
  lenderCardCustom,
  isCustomQuote,
  inArrears,
  applicationType,
}: BuildLenderLoanDetailsPayloadParams) => {
  const activeLenderCard = isCustomQuote ? lenderCardCustom : lenderCard;
  const brokerageAmount = round(activeLenderCard.brokerage.amount.value ?? 0, 2);

  // The quote's fees replace the saved ones outright, so fees removed from the quote are not saved again.
  return mergeWith(
    {},
    loanDetails,
    // Every card shares the calculator slice, so only merge it when it holds this lender's quote.
    isCustomQuote && quoteCalculator.lender === product?.lender ? quoteCalculator : {},
    {
      lender: product?.lender,
      productName: product?.productName,
      productType: product?.productType,
      loanAmount: round(activeLenderCard.loanAmount.value ?? 0, 2),
      rate: activeLenderCard.rate.default.value,
      brokerage: activeLenderCard.brokerage.percentage.value,
      brokerageAmount,
      inArrears,
      fees: formatFeesForSaveQuote(activeLenderCard.fees.application),
      repayments: round(activeLenderCard.repayments.monthly.value ?? 0, 2),
    },
    isCustomQuote
      ? {
          repaymentFrequency: lenderCardCustom.repaymentFrequency,
          ...(lenderCardCustom.referrer ? { referrer: lenderCardCustom.referrer } : {}),
        }
      : {},
    // Brokerage is the broker's commission for consumer and personal applications.
    getIsConsumerOrPersonalApplication(applicationType) ? { commission: brokerageAmount } : {},
    replaceArrays,
  );
};

export { buildLenderLoanDetailsPayload };
